import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function FilterCell({ column, data, value = {}, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(
    Array.isArray(value.select) ? value.select : (value.select ? [value.select] : [])
  );
  const popupRef = useRef(null);
  const buttonRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  const uniqueOptions = Array.from(
    new Set(data.map((row) => row[column.field]).filter((v) => v !== undefined && v !== null && v !== ''))
  );
  const filteredOptions = uniqueOptions.filter((opt) => String(opt).includes(search));

  useEffect(() => {
    setSelected(Array.isArray(value.select) ? value.select : (value.select ? [value.select] : []));
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e) {
      const target = e.target;
      if (popupRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open || !buttonRef.current) return;

    const updatePosition = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom + 6,
        left: Math.max(8, rect.left),
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  if (column.type === 'sum') return null;

  const toggleOption = (opt) => {
    setSelected((prev) => (prev.includes(opt) ? prev.filter((v) => v !== opt) : [...prev, opt]));
  };

  const toggleAll = () => {
    if (selected.length === filteredOptions.length) setSelected([]);
    else setSelected(filteredOptions);
  };

  const toggleEmpty = () => {
    if (selected.includes('')) setSelected(selected.filter((v) => v !== ''));
    else setSelected([...selected, '']);
  };

  const popup = open ? (
    <div
      ref={popupRef}
      className="filter-cell-popup"
      style={{
        position: 'fixed',
        top: `${popupPosition.top}px`,
        left: `${popupPosition.left}px`,
        zIndex: 10000,
      }}
    >
      <div style={{ marginBottom: 8 }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="חיפוש"
          className="filter-cell-search"
        />
      </div>
      <div className="filter-cell-options">
        <div style={{ marginBottom: 4 }}>
          <label className="filter-cell-option">
            <input type="checkbox" checked={selected.length === filteredOptions.length && filteredOptions.length > 0} onChange={toggleAll} />
            (בחר הכל)
          </label>
        </div>
        {filteredOptions.map((opt) => (
          <label key={String(opt)} className="filter-cell-option">
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              onChange={() => toggleOption(opt)}
            />
            {String(opt)}
          </label>
        ))}
        <div style={{ marginTop: 4 }}>
          <label className="filter-cell-option">
            <input type="checkbox" checked={selected.includes('')} onChange={toggleEmpty} />
            (ריקים)
          </label>
        </div>
      </div>
      <div className="filter-cell-actions">
        <button
          type="button"
          className="filter-cell-btn filter-cell-btn--secondary"
          onClick={() => {
            const allOptions = [...uniqueOptions];
            const hasEmpty = data.some((row) => !row[column.field] || row[column.field] === '');
            if (hasEmpty) allOptions.push('');
            setSelected(allOptions);
            onChange('select', allOptions);
            setOpen(false);
          }}
        >
          נקה סינון
        </button>
        <button type="button" className="filter-cell-btn filter-cell-btn--secondary" onClick={() => setOpen(false)}>
          ביטול
        </button>
        <button
          type="button"
          className="filter-cell-btn filter-cell-btn--primary"
          onClick={() => {
            onChange('select', selected);
            setOpen(false);
          }}
        >
          אישור
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="filter-cell-trigger"
        title="סנן עמודה"
        aria-label="סנן עמודה"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 5h18l-7 9v5l-4 2v-7L3 5z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round" />
        </svg>
      </button>
      {popup && createPortal(popup, document.body)}
    </>
  );
}
